import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authClient } from '../lib/authClient';
import { looksLikeDemoClient, isTourDemoActive } from '../services/tourDemo';
import { getTopbarClientsCache, setTopbarClientsCache, fetchAllClients } from '../services/clientApi';

const ClientContext = createContext();

export const useClient = () => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
};

function readSavedClient() {
  const savedClient = localStorage.getItem('selectedClient');
  try {
    const parsed = savedClient ? JSON.parse(savedClient) : null;
    // Never restore the tour example client after a reload / account switch —
    // its meal plans only exist while the playbook tour is active.
    if (parsed && looksLikeDemoClient(parsed) && !isTourDemoActive()) {
      localStorage.removeItem('selectedClient');
      localStorage.removeItem('selectedClientId');
      return null;
    }
    return parsed;
  } catch (error) {
    console.error('Error parsing saved client:', error);
    return null;
  }
}

export const ClientProvider = ({ children }) => {
  const [selectedClient, setSelectedClient] = useState(() => readSavedClient());
  const [clients, setClientsState] = useState(() => getTopbarClientsCache());
  const [isLoadingClients, setIsLoadingClients] = useState(false);

  const setClients = useCallback((newList) => {
    const list = Array.isArray(newList) ? newList : [];
    setTopbarClientsCache(list);
    setClientsState(list);
  }, []);

  useEffect(() => {
    const onTopbarClientsUpdated = (e) => {
      setClientsState(e.detail || []);
    };
    window.addEventListener('topbar-clients-updated', onTopbarClientsUpdated);
    return () => window.removeEventListener('topbar-clients-updated', onTopbarClientsUpdated);
  }, []);

  // Fetch clients if cache is empty
  useEffect(() => {
    if (clients.length === 0) {
      let isMounted = true;
      setIsLoadingClients(true);
      fetchAllClients()
        .then((data) => {
          if (!isMounted) return;
          const list = Array.isArray(data) ? data : (data?.clients || []);
          if (list.length > 0) {
            setClients(list);
          }
        })
        .catch((err) => {
          console.error('ClientContext: error loading clients', err);
        })
        .finally(() => {
          if (isMounted) setIsLoadingClients(false);
        });
      return () => {
        isMounted = false;
      };
    }
  }, [clients.length, setClients]);

  useEffect(() => {
    if (selectedClient && looksLikeDemoClient(selectedClient) && !isTourDemoActive()) {
      setSelectedClient(null);
      return;
    }
    if (selectedClient) {
      localStorage.setItem('selectedClient', JSON.stringify(selectedClient));
      localStorage.setItem('selectedClientId', String(selectedClient.id));
    } else {
      localStorage.removeItem('selectedClient');
      localStorage.removeItem('selectedClientId');
    }
  }, [selectedClient]);

  // Drop stale selection on logout / account switch so the next user starts clean.
  useEffect(() => {
    const { data: { subscription } } = authClient.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        setSelectedClient(null);
        localStorage.removeItem('selectedClient');
        localStorage.removeItem('selectedClientId');
      }
    });
    return () => subscription?.unsubscribe?.();
  }, []);

  // If tour demo ends while Maya is still selected, clear her so meal-plan
  // pages don't silently hit GET /meal-plans/TOURDEMO and show empty.
  useEffect(() => {
    const onDemoChanged = (e) => {
      if (e?.detail?.active) return;
      setSelectedClient((prev) => (looksLikeDemoClient(prev) ? null : prev));
    };
    window.addEventListener('tour-demo-changed', onDemoChanged);
    return () => window.removeEventListener('tour-demo-changed', onDemoChanged);
  }, []);

  return (
    <ClientContext.Provider value={{ selectedClient, setSelectedClient, clients, setClients, isLoadingClients }}>
      {children}
    </ClientContext.Provider>
  );
};
